/*
  TGC — combo picker wiring.

  Renders colour chips and per-colour size boxes for each slot, then adds every
  chosen variant to the cart in ONE /cart/add.js call. It never posts a bundle
  SKU: each line is the real vest variant, so stock, exchanges and Judge.me all
  behave normally. The money comes off via a Shopify automatic discount on the
  source collection — see the comment header in snippets/tgc-vest-combo.liquid.

  Sizes differ per colour on this store, so the size row is rebuilt whenever a
  colour changes rather than rendered once up front.
*/
(function () {
  'use strict';

  /*
    Shopify money_format, e.g. "Rs. {{amount}}". Dawn 15 no longer ships a money
    formatter, so this covers the four standard placeholders. Anything else falls
    back to a plain two-decimal amount rather than printing a raw template.
  */
  function formatMoney(cents, format) {
    var value = (cents || 0) / 100;
    var match = /\{\{\s*(\w+)\s*\}\}/.exec(format || '');
    var placeholder = match ? match[1] : 'amount';

    function group(number, decimals, thousands, decimalMark) {
      var fixed = number.toFixed(decimals);
      var parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts.join(decimalMark) : parts[0];
    }

    var out;
    switch (placeholder) {
      case 'amount_no_decimals':
        out = group(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        out = group(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = group(value, 0, '.', ',');
        break;
      default:
        out = group(value, 2, ',', '.');
    }

    if (!match) return out;
    return format.replace(match[0], out);
  }

  function wire(root) {
    var dataTag = root.querySelector('[data-tgc-combo-data]');
    if (!dataTag) return;

    var data;
    try {
      data = JSON.parse(dataTag.textContent);
    } catch (e) {
      return;
    }

    /*
      Each slot carries its own colour list, so a mixed pack — a tee in slot 1,
      a vest in slot 2 — works exactly like a pack of identical items, which
      simply gets the same list repeated. `colors` below is the flattened union
      of every slot, and is used only for the "from" price shown before anything
      has been picked.
    */
    var slotData = (data && data.slots) || [];
    var colors = [];
    slotData.forEach(function (entry) {
      (entry.colors || []).forEach(function (color) {
        if (colors.indexOf(color) === -1) colors.push(color);
      });
    });
    if (!colors.length) return;

    function colorsFor(index) {
      var entry = slotData[index];
      return (entry && entry.colors) || [];
    }

    var moneyFormat = root.dataset.moneyFormat || 'Rs. {{amount}}';
    var discount = parseInt(root.dataset.discount, 10) || 0;

    var slots = Array.prototype.slice.call(root.querySelectorAll('[data-tgc-combo-slot]'));
    var addButton = root.querySelector('[data-tgc-combo-add]');
    var buyNowButton = root.querySelector('[data-tgc-combo-buynow]');
    var priceBox = root.querySelector('[data-tgc-combo-price]');
    var totalOut = root.querySelector('[data-tgc-combo-total]');
    var subtotalOut = root.querySelector('[data-tgc-combo-subtotal]');
    var saveOut = root.querySelector('[data-tgc-combo-save]');
    var hintOut = root.querySelector('[data-tgc-combo-hint]');
    var errorOut = root.querySelector('[data-tgc-combo-error]');

    // One entry per slot: { color: <colour object>, variant: <size object> }
    var picks = slots.map(function () {
      return { color: null, variant: null };
    });

    function colorHasStock(color) {
      return color.sizes.some(function (size) {
        return size.available;
      });
    }

    /*
      Cheapest in-stock variant on the store, used for the "from" price shown
      before anything is picked. This block is the only price on the combo
      template — Dawn's price block is deliberately not in the block order, so
      there is exactly one price on the page and it is always live.
    */
    function cheapestPrice() {
      var lowest = null;
      colors.forEach(function (color) {
        color.sizes.forEach(function (size) {
          if (size.available && (lowest === null || size.price < lowest)) lowest = size.price;
        });
      });
      return lowest;
    }

    var basePrice = cheapestPrice();

    function findSize(color, label) {
      for (var i = 0; i < color.sizes.length; i++) {
        if (color.sizes[i].label === label) return color.sizes[i];
      }
      return null;
    }

    function renderColors(slot, index) {
      var wrap = slot.querySelector('[data-tgc-combo-colors]');
      wrap.textContent = '';

      colorsFor(index).forEach(function (color) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'tgc-combo__color';
        chip.setAttribute('role', 'radio');
        chip.setAttribute('aria-checked', 'false');
        chip.dataset.colorHandle = color.handle;

        var inStock = colorHasStock(color);
        if (!inStock) {
          chip.disabled = true;
          chip.classList.add('is-out');
        }

        if (color.swatch) {
          var img = document.createElement('img');
          img.src = color.swatch;
          img.alt = '';
          img.loading = 'lazy';
          img.className = 'tgc-combo__color-img';
          chip.appendChild(img);
        }

        var name = document.createElement('span');
        name.className = 'tgc-combo__color-name';
        name.textContent = inStock ? color.name : color.name + ' — sold out';
        chip.appendChild(name);

        chip.addEventListener('click', function () {
          selectColor(index, color);
        });

        wrap.appendChild(chip);
      });
    }

    function renderSizes(slot, index) {
      var wrap = slot.querySelector('[data-tgc-combo-sizes]');
      var color = picks[index].color;
      wrap.textContent = '';

      if (!color) {
        var empty = document.createElement('span');
        empty.className = 'tgc-combo__sizes-empty';
        empty.textContent = 'Pick a colour first';
        wrap.appendChild(empty);
        return;
      }

      color.sizes.forEach(function (size) {
        var box = document.createElement('button');
        box.type = 'button';
        box.className = 'tgc-combo__size';
        box.setAttribute('role', 'radio');
        box.textContent = size.label;

        var chosen = picks[index].variant && picks[index].variant.id === size.id;
        box.setAttribute('aria-checked', chosen ? 'true' : 'false');
        if (chosen) box.classList.add('is-selected');

        if (!size.available) {
          box.disabled = true;
          box.classList.add('is-out');
          box.title = 'Sold out';
        }

        box.addEventListener('click', function () {
          selectSize(index, size);
        });

        wrap.appendChild(box);
      });
    }

    function selectColor(index, color) {
      var previous = picks[index].variant;
      picks[index].color = color;

      /*
        Carry the size across a colour change when that colour stocks it — the
        common path is "same size, different colour", and clearing it every time
        makes the picker feel broken.
      */
      var carried = previous ? findSize(color, previous.label) : null;
      picks[index].variant = carried && carried.available ? carried : null;

      var slot = slots[index];
      slot.querySelectorAll('[data-tgc-combo-colors] .tgc-combo__color').forEach(function (chip) {
        var isThis = chip.dataset.colorHandle === color.handle;
        chip.classList.toggle('is-selected', isThis);
        chip.setAttribute('aria-checked', isThis ? 'true' : 'false');
      });

      renderSizes(slot, index);
      refresh();
    }

    function selectSize(index, size) {
      picks[index].variant = size;
      renderSizes(slots[index], index);
      refresh();
    }

    function describe(pick) {
      if (!pick.color) return 'Not chosen';
      if (!pick.variant) return pick.color.name + ' — pick a size';
      return pick.color.name + ' / ' + pick.variant.label;
    }

    function refresh() {
      slots.forEach(function (slot, index) {
        var state = slot.querySelector('[data-tgc-combo-state]');
        if (state) state.textContent = describe(picks[index]);
        slot.classList.toggle('is-complete', Boolean(picks[index].variant));
      });

      var complete = picks.every(function (pick) {
        return Boolean(pick.variant);
      });

      var subtotal;
      if (complete) {
        subtotal = picks.reduce(function (sum, pick) {
          return sum + pick.variant.price;
        }, 0);
      } else if (basePrice !== null) {
        subtotal = basePrice * picks.length;
      } else {
        subtotal = null;
      }

      if (subtotal === null) {
        priceBox.hidden = true;
      } else {
        var total = Math.max(subtotal - discount, 0);
        totalOut.textContent = (complete ? '' : 'From ') + formatMoney(total, moneyFormat);
        subtotalOut.textContent = formatMoney(subtotal, moneyFormat);
        saveOut.textContent = 'Save ' + formatMoney(subtotal - total, moneyFormat);
        // Nothing struck through when there is no discount configured.
        subtotalOut.hidden = discount <= 0;
        saveOut.hidden = discount <= 0;
        priceBox.hidden = false;
      }

      if (complete) {
        hintOut.textContent = '';
      } else {
        var remaining = picks.filter(function (pick) {
          return !pick.variant;
        }).length;
        hintOut.textContent =
          remaining === picks.length
            ? 'Choose a colour and size for each item.'
            : remaining + (remaining === 1 ? ' item left to choose.' : ' items left to choose.');
      }

      setEnabled(complete);
      hideError();
    }

    function setEnabled(on) {
      // tgc-pdp-buybar.js mirrors this button's disabled state onto the sticky bar.
      addButton.disabled = !on;
      if (buyNowButton) buyNowButton.disabled = !on;
    }

    function showError(message) {
      if (!errorOut) return;
      errorOut.textContent = message;
      errorOut.hidden = false;
    }

    function hideError() {
      if (errorOut) errorOut.hidden = true;
    }

    function setLoading(button, on) {
      if (!button) return;
      button.classList.toggle('loading', on);
      button.disabled = on;
    }

    function cartDrawer() {
      return document.querySelector('cart-drawer');
    }

    function sectionsToRender() {
      var drawer = cartDrawer();
      if (!drawer || typeof drawer.getSectionsToRender !== 'function') return null;
      return drawer.getSectionsToRender().map(function (section) {
        return section.id;
      });
    }

    /*
      One call, both variants. Identical picks merge into a single qty-2 line,
      which is what we want; different picks come through as two lines sharing
      _bundle_id so the cart drawer can group them.
    */
    function addToCart() {
      var bundleId = 'combo-' + Date.now().toString(36);

      var items = picks.map(function (pick) {
        return {
          id: pick.variant.id,
          quantity: 1,
          properties: { _bundle_id: bundleId },
        };
      });

      var body = { items: items };
      var sections = sectionsToRender();
      if (sections) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      return fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then(function (response) {
          return response.json().then(function (parsed) {
            if (!response.ok) {
              // 422 here means stock ran out between page load and this click.
              throw new Error(parsed.description || parsed.message || 'That size just sold out.');
            }
            return parsed;
          });
        });
    }

    addButton.addEventListener('click', function () {
      if (addButton.disabled) return;
      hideError();
      setLoading(addButton, true);

      addToCart()
        .then(function (parsed) {
          var drawer = cartDrawer();
          var hasDrawerSection = parsed.sections && parsed.sections['cart-drawer'];

          if (drawer && hasDrawerSection && typeof drawer.renderContents === 'function') {
            drawer.renderContents(parsed);

            /*
              Belt and braces. cart-drawer.js clears is-empty off <cart-drawer>
              itself as of the fix in renderContents; this line keeps the combo
              page working if that ever regresses, since a stale is-empty hides
              the drawer header and squeezes the line items out of view.
            */
            drawer.classList.remove('is-empty');
          } else {
            // No drawer section to paint — a full cart page is better than a
            // drawer that opens onto nothing.
            window.location.href = '/cart';
          }
        })
        .catch(function (error) {
          showError(error.message);
        })
        .then(function () {
          setLoading(addButton, false);
          setEnabled(true);
        });
    });

    if (buyNowButton) {
      buyNowButton.addEventListener('click', function () {
        if (buyNowButton.disabled) return;
        hideError();
        setLoading(buyNowButton, true);

        addToCart()
          .then(function () {
            /*
              GoKwik defines onCheckoutClick on its remote script and reads the
              whole cart, so it handles a two-line combo. Its PDP buy-now handler
              (onBuyNowClick) reads a single selected variant off the product form
              and cannot, which is why this goes through the cart-level one.
              Falls back to /checkout if GoKwik has not loaded or is bypassed for
              international traffic.
            */
            if (typeof window.onCheckoutClick === 'function') {
              window.onCheckoutClick(buyNowButton);
            } else {
              window.location.href = '/checkout';
            }
          })
          .catch(function (error) {
            showError(error.message);
            setLoading(buyNowButton, false);
            setEnabled(true);
          });
      });
    }

    slots.forEach(function (slot, index) {
      renderColors(slot, index);
      renderSizes(slot, index);
    });
    refresh();
  }

  function init() {
    document.querySelectorAll('[data-tgc-combo]').forEach(wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
